import { memo } from 'react';
import { GiveawayProjection, GiveawayStatus } from '@daredrop-com/rewards-api-contract';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import { getCookie } from 'root/src/client/v2/browser/helpers/cookies';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { RedesignModalShell } from 'root/src/client/v2/common/components/RedesignModalShell';
import { Badge } from 'root/src/client/v2/common/components/ui/badge';
import { Skeleton } from 'root/src/client/v2/common/components/ui/skeleton';
const formatDate = (value) => (value
    ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : '---');
const getStatusBadge = (status) => {
    if (status === GiveawayStatus.Resolved) {
        return { label: 'Completed', className: 'border-neon-green/30 bg-neon-green/10 text-neon-green' };
    }
    if (status === GiveawayStatus.Active) {
        return { label: 'Live', className: 'border-neon-pink/30 bg-neon-pink/10 text-neon-pink' };
    }
    return { label: 'Ended', className: 'border-white/10 bg-white/[0.06] text-muted-foreground' };
};
const Stat = ({ label, value }) => (<span>{label} <span className="font-semibold text-foreground">{value}</span></span>);
// Redesign modal listing the creator's past giveaways (date, status, entrants / entries / winners).
// Reads GET_USER_GIVEAWAYS for the logged-in user, newest first.
export const RedesignGiveawayHistoryModal = memo(() => {
    var _a, _b;
    const userId = getCookie('userId');
    const query = trpc.giveaways.getUserGiveaways.useQuery({ userId, limit: 20, projection: GiveawayProjection.Creator }, { enabled: Boolean(userId) });
    const giveaways = [...((_b = (_a = query.data) === null || _a === void 0 ? void 0 : _a.giveaways) !== null && _b !== void 0 ? _b : [])]
        .sort((a, b) => { var _a, _b, _c, _d; return new Date((_b = (_a = b.endDate) !== null && _a !== void 0 ? _a : b.startDate) !== null && _b !== void 0 ? _b : 0).getTime() - new Date((_d = (_c = a.endDate) !== null && _c !== void 0 ? _c : a.startDate) !== null && _d !== void 0 ? _d : 0).getTime(); });
    return (<RedesignModalShell className="sm:max-w-lg">
			<div className="flex w-full flex-col items-center gap-3 text-center">
				<div className="flex h-12 w-12 items-center justify-center rounded-full border border-solid border-neon-cyan/30 bg-neon-cyan/10">
					<HistoryRoundedIcon sx={{ fontSize: 24 }} className="text-neon-cyan"/>
				</div>
				<h2 className="font-display text-xl font-bold text-foreground">Giveaway history</h2>
			</div>

			<div className="mt-2 max-h-[60vh] w-full space-y-3 overflow-y-auto pr-1">
				{query.isLoading && ['a', 'b', 'c'].map(id => <Skeleton key={id} className="h-20 w-full rounded-xl"/>)}

				{!query.isLoading && giveaways.length === 0 && (<p className="py-10 text-center text-sm text-muted-foreground">No giveaways yet.</p>)}

				{!query.isLoading && giveaways.map((giveaway, index) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
            const badge = getStatusBadge(giveaway.status);
            const winner = (_c = (_b = (_a = giveaway.vouchers) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.owner) === null || _c === void 0 ? void 0 : _c.name;
            return (<div key={(_d = giveaway.id) !== null && _d !== void 0 ? _d : index} className="rounded-xl border border-solid border-white/[0.08] bg-white/[0.02] p-3">
							<div className="flex items-center justify-between gap-2">
								<span className="text-sm font-semibold text-foreground">{formatDate((_e = giveaway.endDate) !== null && _e !== void 0 ? _e : giveaway.startDate)}</span>
								<Badge variant="outline" className={badge.className}>{badge.label}</Badge>
							</div>
							<div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
								<Stat label="Entrants" value={(_g = (_f = giveaway.stats) === null || _f === void 0 ? void 0 : _f.participantCount) !== null && _g !== void 0 ? _g : 0}/>
								<Stat label="Entries" value={(_j = (_h = giveaway.stats) === null || _h === void 0 ? void 0 : _h.entryCount) !== null && _j !== void 0 ? _j : 0}/>
								<Stat label="Winners" value={(_l = (_k = giveaway.vouchers) === null || _k === void 0 ? void 0 : _k.length) !== null && _l !== void 0 ? _l : 0}/>
							</div>
							{winner && (<p className="mt-1.5 text-xs text-muted-foreground">Winner: <span className="font-medium text-foreground">{winner}</span></p>)}
						</div>);
        })}
			</div>
		</RedesignModalShell>);
});
